import { useAuth0 } from "@auth0/auth0-react";
import { Icon, Tooltip } from "@chakra-ui/react";
import { ReactComponent as AutoFixIcon } from "assets/icons/auto-fix.svg";
import { ReactComponent as UndoIcon } from "assets/icons/undo.svg";
import ActionButton from "components/chat/messages-area/new-message-input-box/ActionButton";
import MessageDomain from "entities/domain/conversations/message-domain";
import useAgentsStore from "hooks/use-agents-store";
import useMerchantStore from "hooks/use-merchant-store";
import React, { forwardRef, RefObject, useEffect, useState } from "react";
import InboxService from "services/inbox";

interface MagicWandProps {
  lastMessage?: MessageDomain;
  message?: string;
  setMessage: (m: string) => void;
  setIsLoading?: (loading: boolean) => void;
  trackAnalytics: (name: string) => void;
}

const MagicWand = forwardRef<HTMLButtonElement, MagicWandProps>(
  (
    {
      lastMessage,
      message,
      setMessage,
      setIsLoading,
      trackAnalytics,
    }: MagicWandProps,
    ref
  ) => {
    const auth0Context = useAuth0();
    const { merchant } = useMerchantStore();
    const { currentAgent } = useAgentsStore();

    const [cachedMessage, setCachedMessage] = useState<string | undefined>();
    const [cachedImprovedMessage, setCachedImprovedMessage] = useState<
      string | undefined
    >();
    const [showUndoButton, setShowUndoButton] = useState<boolean>(false);
    const [canCreateAutoSuggestion, setCanCreateAutoSuggestion] =
      useState<boolean>(false);

    const generateAutoReply = async () => {
      if (!lastMessage) {
        return;
      }

      if (typeof setIsLoading !== "undefined") {
        setIsLoading(true);
      }

      trackAnalytics("generate_auto_reply_attempt");

      try {
        const suggestedReply = (
          await InboxService.getAutoReplyMessage(
            auth0Context,
            lastMessage.conversationId
          )
        ).suggested_reply;

        setMessage(suggestedReply);

        trackAnalytics("generate_auto_reply_attempt_succeeded");
      } catch (error) {
        trackAnalytics("generate_auto_reply_attempt_failed");

        // eslint-disable-next-line
        console.log("Couldn't generate automatic reply from server");
      } finally {
        if (typeof setIsLoading !== "undefined") {
          setIsLoading(false);
        }
      }
    };

    const executeAutoFix = async () => {
      if (!message) {
        return;
      }

      if (message === cachedMessage && cachedImprovedMessage) {
        trackAnalytics("get_cached_improved_message");
        setMessage(cachedImprovedMessage);

        return;
      }

      if (message === cachedImprovedMessage) {
        trackAnalytics("get_original_message_back");
        setMessage(cachedMessage!);

        return;
      }

      setCachedMessage(message);

      if (typeof setIsLoading !== "undefined") {
        setIsLoading(true);
      }

      let updatedMessage: string = message;

      try {
        trackAnalytics("get_improved_message_attempt");

        updatedMessage = (
          await InboxService.getAutoFixedMessage(auth0Context, message)
        ).prettified_text;

        trackAnalytics("get_improved_message_attempt_succeeded");
      } catch (error) {
        // eslint-disable-next-line
        console.log("Couldn't get suggestions from server");
      } finally {
        if (typeof setIsLoading !== "undefined") {
          setIsLoading(false);
        }
      }

      setCachedImprovedMessage(updatedMessage);
      setMessage(updatedMessage);
    };

    useEffect(() => {
      setShowUndoButton(message === cachedImprovedMessage);
    }, [message, cachedImprovedMessage]);

    useEffect(() => {
      setCanCreateAutoSuggestion(
        Boolean(
          lastMessage &&
            lastMessage.isIncoming &&
            lastMessage.body &&
            !lastMessage.isNotification() &&
            !lastMessage.getAttachmentType() &&
            !message
        )
      );
    }, [lastMessage, message]);

    return showUndoButton ? (
      <ActionButton
        action={() => executeAutoFix()}
        ariaLabel="Undo changes made with AI"
        ref={ref}
      >
        <Tooltip
          label={message ? "Undo changes made by AI" : null}
          borderRadius="1rem"
          px={3}
          py={1}
          backgroundColor="rgba(54, 178, 250, 0.9)"
        >
          <Icon as={UndoIcon} />
        </Tooltip>
      </ActionButton>
    ) : (
      <ActionButton
        ref={ref}
        action={() => {
          if (canCreateAutoSuggestion) {
            generateAutoReply();
          } else {
            executeAutoFix();
          }
        }}
        ariaLabel={
          canCreateAutoSuggestion
            ? "Generate AI reply suggestion"
            : "Improve message with AI"
        }
        isDisabled={!message && !canCreateAutoSuggestion}
      >
        <Tooltip
          label={message ? "Get help constructing a message from AI" : null}
          borderRadius="1rem"
          px={3}
          py={1}
          backgroundColor="rgba(54, 178, 250, 0.9)"
        >
          <Icon as={AutoFixIcon} />
        </Tooltip>
      </ActionButton>
    );
  }
);

export default MagicWand;
